import { Client } from "@/Common/Api/Client";
import { ErrorMessage } from "@/Common/Api/Dto/ErrorMessage";
import { FlatLookupView } from "@/Common/Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { ValidationRule } from "@/Common/Api/Validation/Dto/Responses/ValidationRule";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ErrorMessageComponent } from "@/Common/Components/Errors/ErrorMessageComponent";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { TextboxMaskedControl } from "@/Common/Components/Inputs/TextboxMaskedControl";
import { InformationLoadingComponent } from "@/Common/Components/Loader/InformationLoadingComponent";
import { CreditCardHelper } from "@/Common/Helpers/CreditCardHelper";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { PaymentGatewayPaymentProfileDto } from "@/Common/Models/Portal/MedfarPortal";
import { PaymentType } from "@/Common/Models/Portal/MedfarPortalEnums";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { ServerEvent } from "@/Common/Stores/Actions/ServerEvent";

interface ISubscriptionPaymentInformation_EditPopupProps {
    patientContactId: string;
    selectedPaymentProfile: PaymentGatewayPaymentProfileDto;
    onCloseCallback: () => void;
    onSuccessCallback: (response: ServerEvent) => void;

    validationDebounceMs?: number;
}

class SubscriptionPaymentInformation_EditPopupState {
    public isLoading: boolean;
    public errors: string[];

    public validationDebounceMs = 500;

    public canChangeCardNumber = true;
    public cardNumber: string = "";
    public cardNumberInputMask: string;

    public expirationValue = "";
    public expirationYear?: number;
    public expirationMonth?: number;

    public cvv: string = "";
    public cvvMaxLength: number = 4;

    public cardholderName: string = "";

    public company: string = "";
    public address: string = "";
    public city: string = "";
    public zipCode: string = "";
    public regionLookupId: string = "";
    public countryLookupId: string = "";

    public cardNumberIsValid = false;
    public cardholderNameIsValid = false;
    public expirationIsValid = false;
    public cvvIsValid = false;
    public addressIsValid = false;
    public cityIsValid = false;
    public zipCodeIsValid = false;
    public isValid = false;

    public currentIsDefault: boolean;
    public isDefault = false;

    public zipValidationRule: ValidationRule;

    constructor(fields?: Partial<SubscriptionPaymentInformation_EditPopupState>) {
        (Object as any).assign(this, fields);
    }
}

class SubscriptionPaymentInformation_EditPopup extends BaseReactComponent<ISubscriptionPaymentInformation_EditPopupProps, SubscriptionPaymentInformation_EditPopupState> {
    private readonly componentId = "portal_trx_paymentInfo";
    private readonly countryLookups = cacheStore.GetCountryLookups();
    private readonly validationHelper = new ValidationHelper();
    private readonly creditCardHelper = new CreditCardHelper();

    constructor(props: ISubscriptionPaymentInformation_EditPopupProps) {
        super(props);

        let monthList: number[] = [];
        for (let i = 1; i <= 12; i++) {
            monthList[i] = i;
        }

        const countryLookup = cacheStore.GetDefaultCountryForActiveSite();
        const regionLookup = cacheStore.GetDefaultRegionForActiveSite();
        const zipValidationRule = countryLookup && this.validationHelper.getPostalCodeValidationRule(countryLookup.LookupID, regionLookup && regionLookup.LookupID);

        this.state = new SubscriptionPaymentInformation_EditPopupState({
            countryLookupId: countryLookup && countryLookup.LookupID,
            regionLookupId: regionLookup && regionLookup.LookupID,
            zipValidationRule: zipValidationRule
        });
    }

    public componentDidMount(): void {
        super.setOverlay("SubscriptionPaymentInformation_EditPopup");
        this.loadStateFromProps(this.props);

        const elem = $(`#${this.componentId}`);
        const cnt = document.body;
        var dragOptions = {
            containment: cnt,
            handle: ".dragHandle",
            stop() {
                elem.draggable("destroy");
                elem.css("height", "auto");
                window.setTimeout(() => {
                    elem.draggable(dragOptions);
                }, 50);
            }
        };
        elem.draggable(dragOptions);
        $(window).trigger("resize");
    }

    public render(): JSX.Element {
        return (
            <div className="popup portal-popup" id={this.componentId}>
                <div className="portal-messages-header">
                    <div className="itemGrow dragHandle">
                        <h3 className="thin-font">{this.props.selectedPaymentProfile ? cacheStore.GetString("EditPaymentMethodLabel") : cacheStore.GetString("AddPaymentMethodLabel")}</h3>
                    </div>
                    <div>
                        <a className="close" onClick={() => this.onCloseCallback()} aria-label="close" style={{ paddingTop: "10px" }}>×</a>
                    </div>
                </div>
                <div className="p-body">
                    {this.state.isLoading &&
                        <InformationLoadingComponent smallVersion={true} customMessage={cacheStore.GetString("WaitForPortalLabel")} />
                    }
                    {this.state.errors && this.state.errors.length &&
                        <ErrorMessageComponent errors={this.state.errors} />
                    }
                    <div className="scroll-form">
                        <div className="flex-container p-crf">
                            <div className="c1"><span>{cacheStore.GetString("NumberLabel")}</span></div>
                            <div className="c2">
                                {this.state.canChangeCardNumber && (
                                    <TextboxMaskedControl
                                        onChangeFunction={(unmaskedValue: string) => this.onCreditCardChange(unmaskedValue)}
                                        isValid={this.state.cardNumberIsValid}
                                        value={this.state.cardNumber}
                                        disabled={this.state.isLoading}
                                        mask={this.state.cardNumberInputMask}
                                    />
                                )}
                                {!this.state.canChangeCardNumber && (
                                    <div className="form-group">
                                        <span className="bold">{this.state.cardNumber} ({PaymentType[this.props.selectedPaymentProfile.PaymentType]})</span>
                                        <button className="btn btn-primary marginleft10" onClick={() => this.onChangeNumberClick()}>{cacheStore.GetString("ChangeLabel")}</button>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="flex-container p-crf ">
                            <div className="c1"><span>{cacheStore.GetString("ExpirationLabel")}</span></div>
                            <TextboxMaskedControl
                                onChangeFunction={(unmaskedValue: string) => this.onExpirationChange(unmaskedValue)}
                                isValid={this.state.expirationIsValid}
                                value={this.state.expirationValue}
                                disabled={this.state.isLoading}
                                mask="99 / 99"
                                debounceMs={this.state.validationDebounceMs}
                            />
                            {this.state.canChangeCardNumber &&
                                <div className="c1"><span>CVV</span></div>
                            }
                            {this.state.canChangeCardNumber && (
                                <TextboxControl
                                    type="tel"
                                    disabled={this.state.isLoading}
                                    maxLength={this.state.cvvMaxLength}
                                    value={this.state.cvv}
                                    onChange={(text: string) => this.onCvvChange(text)}
                                    containerCssClass={this.state.cvvIsValid ? "form-group" : "form-group has-error"}
                                    className="form-control"
                                    debounceMs={this.state.validationDebounceMs}
                                />
                            )}
                        </div>
                        <div className="flex-container p-crf">
                            <div className="c1"><span>{cacheStore.GetString("CardholderNameLabel")}</span></div>
                            <div className="c2">
                                <TextboxControl
                                    disabled={this.state.isLoading}
                                    value={this.state.cardholderName}
                                    onChange={(text: string) => this.onCardholderNameChange(text)}
                                    containerCssClass={this.state.cardholderNameIsValid ? "form-group" : "form-group has-error"}
                                    className="form-control"
                                    debounceMs={this.state.validationDebounceMs}
                                />
                            </div>
                        </div>
                        <hr />
                        <div className="flex-container p-crf">
                            <div className="c1"><span>{cacheStore.GetString("CompanyLabel")}</span></div>
                            <div className="c2">
                                <TextboxControl
                                    disabled={this.state.isLoading}
                                    value={this.state.company}
                                    onChange={(text: string) => this.onCompanyChange(text)}
                                    containerCssClass="form-group"
                                    className="form-control"
                                />
                            </div>
                        </div>
                        <div className="flex-container p-crf">
                            <div className="c1"><span>{cacheStore.GetString("AddressLabel")}</span></div>
                            <div className="c2">
                                <TextboxControl
                                    disabled={this.state.isLoading}
                                    value={this.state.address}
                                    onChange={(text: string) => this.onAddressChange(text)}
                                    containerCssClass={this.state.addressIsValid ? "form-group" : "form-group has-error"}
                                    className="form-control"
                                    debounceMs={this.state.validationDebounceMs}
                                />
                            </div>
                        </div>
                        <div className="flex-container p-crf">
                            <div className="c1"><span>{cacheStore.GetString("CityLabel")}</span></div>
                            <div className="c2">
                                <TextboxControl
                                    disabled={this.state.isLoading}
                                    value={this.state.city}
                                    onChange={(text: string) => this.onCityChange(text)}
                                    containerCssClass={this.state.cityIsValid ? "form-group" : "form-group has-error"}
                                    className="form-control"
                                    debounceMs={this.state.validationDebounceMs}
                                />
                            </div>
                        </div>
                        <div className="flex-container p-crf">
                            <div className="c1"><span>{cacheStore.GetString("CountryLabel")}</span></div>
                            <div className="c2">
                                <select disabled={this.state.isLoading} onChange={(e: any) => this.onCountryChange(e.target.value)} className="form-control myle-control" value={this.state.countryLookupId}>
                                    {this.countryLookups.map((z: FlatLookupView) => <option key={z.LookupID} value={z.LookupID}>{z.GetValue()}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="flex-container p-crf">
                            <div className="c1"><span>{cacheStore.GetString("StateProvinceLabel")}</span></div>
                            <div className="c2">
                                <select disabled={this.state.isLoading} onChange={(e: any) => this.onRegionChange(e.target.value)} className="form-control myle-control" value={this.state.regionLookupId}>
                                    {this.getRegionLookupsForSelectedCountry().sortByProperty(p => p.GetValue()).map((z: FlatLookupView) => <option key={z.LookupID} value={z.LookupID}>{z.GetValue()}</option>)}
                                </select>
                            </div>
                        </div>

                        <div className="flex-container p-crf">
                            <div className="c1"><span>{cacheStore.GetString("ZipPostalCodeLabel")}</span></div>
                            <div className="c2">
                                <TextboxMaskedControl
                                    isValid={this.state.zipCodeIsValid}
                                    allowEmpty={false}
                                    value={this.state.zipCode}
                                    onChangeFunction={(zipCode: string) => this.onZipCodeChange(zipCode)}
                                    debounceMs={this.state.validationDebounceMs}
                                    mask={this.state.zipValidationRule && this.state.zipValidationRule.EditMaskPattern}
                                    disabled={this.state.isLoading}
                                    validationErrorMessage={cacheStore.GetLocalizedValueByLookupId(this.state.zipValidationRule && this.state.zipValidationRule.ValidationErrorMessageLookupId)}
                                />
                            </div>
                        </div>

                        <div className="flex-container p-crf">
                            <div className="c1"><span>{cacheStore.GetString("PrimaryPaymentMethodLabel")}</span></div>
                            <div className="c2">
                                <input
                                    type="checkbox"
                                    disabled={this.state.isLoading || this.state.currentIsDefault}
                                    checked={this.state.isDefault}
                                    onChange={(e: any) => this.onIsDefaultChange(e.target.checked)}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="p-footer">
                    <div className="col-lg-12 text-right">
                        <button className="btn btn-default push-right-5" onClick={() => this.onCloseCallback()}>{cacheStore.GetString("CancelLabel")}<span /></button>
                        <button className="btn btn-primary" disabled={this.state.isLoading || !this.state.isValid} onClick={() => this.onSavePaymentInfo()}>{cacheStore.GetString("SaveLabel")}<span /></button>
                    </div>
                </div>
            </div>
        );
    }

    private getRegionLookupsForSelectedCountry(): FlatLookupView[] {
        const selectedCountry = this.state.countryLookupId &&
            this.countryLookups.firstOrDefault(p => p.LookupID === this.state.countryLookupId);

        return cacheStore
            .GetCountryRegionLookups(selectedCountry || this.countryLookups[0])
            .sortByProperty(p => p.GetValue());
    }

    private mutateStateWithValidation(mutation: (state: SubscriptionPaymentInformation_EditPopupState) => void): void {
        this.mutateState(mutation, () => this.validateForm());
    }

    private loadStateFromProps(props: ISubscriptionPaymentInformation_EditPopupProps): void {
        const profile = props.selectedPaymentProfile;

        this.mutateStateWithValidation(
            state => {
                if (profile) {
                    state.cardNumber = profile.CardNumber;
                    state.cardholderName = profile.CardholderName;
                    state.expirationYear = profile.ExpirationYear;
                    state.expirationMonth = profile.ExpirationMonth;
                    state.expirationValue = `${profile.ExpirationMonth.toString().padLeft("0", 2)}${(profile.ExpirationYear % 100).toString().padLeft("0", 2)}`;

                    state.currentIsDefault = profile.IsDefault;
                    state.isDefault = profile.IsDefault;

                    state.canChangeCardNumber = false;

                    if (profile.BillingAddress) {
                        state.company = profile.BillingAddress.Company || "";
                        state.address = profile.BillingAddress.StreetAddress || "";
                        state.city = profile.BillingAddress.City || "";
                        state.zipCode = profile.BillingAddress.PostCode || "";

                        const countryLookupCode = cacheStore.GetCountryLookupCodeByISOCodeAlpha2(profile.BillingAddress.CountryCode);
                        let countryLookup: FlatLookupView;

                        if (countryLookupCode) {
                            countryLookup = cacheStore.GetCountryLookups().firstOrDefault(p => p.LookupCode === countryLookupCode);
                        }
                        else {
                            // some billing addresses are saved with the full country name
                            // try to resolve the country by name
                            const upperCountry = profile.BillingAddress.CountryCode
                                && profile.BillingAddress.CountryCode.toUpperCase();

                            const countryMatches = cacheStore.GetCountryLookups()
                                .filter(p => (p.en && p.en.toUpperCase() === upperCountry)
                                    || (p.fr && p.fr.toUpperCase() === upperCountry)
                                    || (p.esMX && p.esMX.toUpperCase() === upperCountry)
                                );

                            if (countryMatches.length === 1)
                                countryLookup = countryMatches[0];
                        }

                        if (countryLookup) {
                            state.countryLookupId = countryLookup.LookupID;
                            state.regionLookupId = cacheStore.GetStateLookupIdFromStateCode(
                                profile.BillingAddress.Province,
                                cacheStore.GetCountryRegionLookups(countryLookup)
                            );

                            state.zipValidationRule = this.validationHelper.getPostalCodeValidationRule(state.countryLookupId, state.regionLookupId);
                        }
                    }
                }

                if (!IsNullOrUndefined(props.validationDebounceMs))
                    state.validationDebounceMs = props.validationDebounceMs;
            }
        );
    }

    private ccValidationTimeoutHandle: number;

    private onCreditCardChange(value: string): void {
        // remove all whitespace
        const cardNumber = IsNullOrWhiteSpace(value) ? "" : value.split(/\s/).join("");

        this.mutateStateWithValidation(state => {
            state.cardNumber = cardNumber;

            const cardInfo = this.creditCardHelper.getPossibleCardInfo(cardNumber);

            if (cardInfo) {
                state.cardNumberInputMask = cardInfo.inputMask;
                state.cvvMaxLength = cardInfo.codeLength;
            }
            else {
                state.cardNumberInputMask = null;
                state.cvvMaxLength = 4;
            }
        });

        if (this.ccValidationTimeoutHandle)
            clearTimeout(this.ccValidationTimeoutHandle);

        this.ccValidationTimeoutHandle = window.setTimeout(
            () => this.mutateState(state => state.cardNumberIsValid = this.creditCardHelper.validateCardNumber(state.cardNumber)),
            this.state.validationDebounceMs
        );
    }

    private onExpirationChange(unmaskedValue: string): void {
        // unmasked value is MMYY
        const monthPart = unmaskedValue.substr(0, 2);
        const yearPart = unmaskedValue.substr(2, 2);

        this.mutateStateWithValidation(state => {
            state.expirationValue = unmaskedValue;
            state.expirationMonth = monthPart.length === 2 ? parseInt(monthPart) : null;
            state.expirationYear = yearPart.length === 2 ? 2000 + parseInt(yearPart) : null;
            state.expirationIsValid = this.validateExpirationDate(state.expirationYear, state.expirationMonth);
        });
    }

    private validateExpirationDate(expirationYear?: number, expirationMonth?: number): boolean {
        if (expirationYear === null ||
            expirationMonth === null ||
            expirationMonth < 1 ||
            expirationMonth > 12)
            return false;

        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().getMonth() + 1;

        return expirationYear > currentYear ||
            (expirationYear === currentYear && expirationMonth >= currentMonth);
    }

    private onCvvChange(cvv: string): void {
        this.mutateStateWithValidation(state => {
            state.cvv = cvv;
            state.cvvIsValid = this.validateCvv(cvv);
        });
    }

    private validateCvv(cvv: string): boolean {
        return /^[0-9]{3,4}$/.test(cvv);
    }

    private onCardholderNameChange(cardholderName: string): void {
        this.mutateStateWithValidation(state => {
            state.cardholderName = cardholderName;
            state.cardholderNameIsValid = !IsNullOrWhiteSpace(cardholderName);
        });
    }

    private onAddressChange(address: string): void {
        this.mutateStateWithValidation(state => {
            state.address = address;
            state.addressIsValid = !IsNullOrWhiteSpace(address);
        });
    }

    private onCompanyChange(company: string): void {
        this.mutateStateWithValidation(state => state.company = company);
    }

    private onCityChange(city: string): void {
        this.mutateStateWithValidation(state => {
            state.city = city;
            state.cityIsValid = !IsNullOrWhiteSpace(city);
        });
    }

    private onZipCodeChange(value: string): void {
        this.mutateStateWithValidation(state => {
            state.zipCode = value || "";
            state.zipCodeIsValid = this.validateZipCode(value, state.zipValidationRule);
        });
    }

    private validateZipCode(zipCode: string, zipValidationRule: ValidationRule): boolean {
        if (IsNullOrWhiteSpace(zipCode))
            return false;

        if (!zipValidationRule)
            return true;

        return ValidationHelper.isExactMatch(zipCode, new RegExp(zipValidationRule.ValidationRegexPattern, "i"));
    }

    private onRegionChange(regionLookupId: string): void {
        this.setCountryAndRegion(this.state.countryLookupId, regionLookupId);
    }

    private onCountryChange(countryLookupId: string): void {
        this.setCountryAndRegion(countryLookupId, this.state.regionLookupId);
    }

    private setCountryAndRegion(countryLookupId: string, regionLookupId: string): void {
        const countryLookups = cacheStore.GetCountryLookups();
        const countryLookup = countryLookups.firstOrDefault(p => p.LookupID === countryLookupId) || countryLookups[0];

        const regionLookups = cacheStore.GetCountryRegionLookups(countryLookup);
        const regionLookup = regionLookups.firstOrDefault(p => p.LookupID === regionLookupId) || regionLookups[0];

        const zipValidationRule = this.validationHelper.getPostalCodeValidationRule(countryLookup.LookupID, regionLookup.LookupID);

        this.mutateStateWithValidation(state => {
            state.countryLookupId = countryLookup.LookupID;
            state.regionLookupId = regionLookup.LookupID;
            state.zipValidationRule = zipValidationRule;
        });
    }

    private onIsDefaultChange(isDefault: boolean): void {
        if (this.state.currentIsDefault) return;

        this.mutateStateWithValidation(state => state.isDefault = isDefault);
    }

    private validateForm(afterValidationCallback?: (isValid: boolean) => void): void {
        const cardNumberIsValid = !this.state.canChangeCardNumber || this.creditCardHelper.validateCardNumber(this.state.cardNumber);
        const cvvIsValid = !this.state.canChangeCardNumber || this.validateCvv(this.state.cvv);
        const cardholderNameIsValid = !IsNullOrWhiteSpace(this.state.cardholderName);
        const expirationIsValid = this.validateExpirationDate(this.state.expirationYear, this.state.expirationMonth);
        const addressIsValid = !IsNullOrWhiteSpace(this.state.address);
        const cityIsValid = !IsNullOrWhiteSpace(this.state.city);
        const zipCodeIsValid = this.validateZipCode(this.state.zipCode, this.state.zipValidationRule);
        const isValid = cardNumberIsValid &&
            cvvIsValid &&
            cardholderNameIsValid &&
            expirationIsValid &&
            addressIsValid &&
            cityIsValid &&
            zipCodeIsValid;

        this.mutateState(
            state => {
                state.cardNumberIsValid = cardNumberIsValid;
                state.cvvIsValid = cvvIsValid;
                state.cardholderNameIsValid = cardholderNameIsValid;
                state.expirationIsValid = expirationIsValid;
                state.addressIsValid = addressIsValid;
                state.cityIsValid = cityIsValid;
                state.zipCodeIsValid = zipCodeIsValid;
                state.isValid = isValid;
            },
            () => afterValidationCallback && afterValidationCallback(isValid)
        );
    }

    private onSavePaymentInfo(): void {
        this.validateForm(
            isValid => {
                if (!isValid) return;

                const countryInfo = cacheStore.getCountryByLookupId(this.state.countryLookupId);
                if (!countryInfo) throw Error(`Country info not found for lookup ${this.state.countryLookupId}`);

                const requestData: any = {
                    CardholderName: this.state.cardholderName,
                    ExpirationYear: this.state.expirationYear,
                    ExpirationMonth: this.state.expirationMonth,

                    Company: this.state.company,
                    Address: this.state.address,
                    City: this.state.city,
                    ProvinceCode: cacheStore.GetStateCodeFromStateLookupId(this.state.regionLookupId),
                    PostalCode: this.state.zipCode,
                    Country: countryInfo.Alpha2Code,
                    PatientContactId: this.props.patientContactId,
                    PaymentGatewayPaymentProfileId: this.props.selectedPaymentProfile ? this.props.selectedPaymentProfile.PaymentGatewayPaymentProfileId : null,
                    IsEdit: true,
                    IsDefault: this.state.isDefault
                };

                if (this.state.canChangeCardNumber) {
                    requestData.HasCardNumberChanged = true;
                    requestData.CardNumber = this.state.cardNumber;
                    requestData.Cvv = this.state.cvv;
                }

                this.mutateState(state => state.isLoading = true);

                Client.Post(
                    ServerOperations.Server.Portal_Proxy_PostPaymentForm,
                    requestData,
                    (response, errors) => this.onSaveSuccess(response, errors),
                    ex => this.onError(ex)
                );
            }
        );
    }

    private onError(ex: any): void {
        this.mutateState(state => {
            state.isLoading = false;
            state.errors = [ex.toString()];
        });
    }

    private onSaveSuccess(response: ServerEvent, errors: ErrorMessage[]): void {
        if (errors && errors.length) {
            this.mutateState(state => {
                state.isLoading = false;
                state.errors = errors.map(error => error.Message);
            });
            return;
        }

        this.mutateState(
            state => {
                state.isLoading = false;
                state.errors = [];
            },
            () => this.props.onSuccessCallback && this.props.onSuccessCallback(response)
        );
    }

    private onCloseCallback(): void {
        this.props.onCloseCallback && this.props.onCloseCallback();
    }

    private onChangeNumberClick(): void {
        this.mutateState(state => {
            state.canChangeCardNumber = true;
            state.cardNumber = "";
            state.cvv = "";
            state.isValid = false;
        });
    }
}

export { SubscriptionPaymentInformation_EditPopup };
